import { ReactNode, useContext, useEffect, useRef, useState } from "react"
import ReactDOM from "react-dom"
import Suggestions from "./Suggestions"
import { FilterMenuItem, FlexibleTableHeaderColumn } from "../../flexible-table-items.interface"
import { FiltersContext } from "../../context/table.context"
import { computePosition } from "@floating-ui/react"
import { flip, shift } from "@floating-ui/dom"
import Icon from "src/shared/icons"
import { usePortalLayer } from "src/shared/providers/PortalLayerProvider"

interface HeaderSuggetionsProps {
    header: FlexibleTableHeaderColumn
    onSelectFilter: ({
        filter,
        fromHeader,
        isUnique,
        valueToDisplay,
    }: {
        filter: string
        fromHeader: string
        valueToDisplay?: ReactNode
        isUnique?: boolean
    }) => void
    onBottomReached?: () => void
    debounceSearchMSTime?: number
}

function HeaderSuggetions({
    header,
    onSelectFilter,
    onBottomReached,
    debounceSearchMSTime,
}: HeaderSuggetionsProps): JSX.Element {
    const [visible, setVisible] = useState<boolean>(false)

    const dropdownRef = useRef<HTMLDivElement>(null)
    const scrollRef = useRef<HTMLDivElement>(null)
    const headerRef = useRef<HTMLTableCellElement>(null)
    const portalLayer = usePortalLayer()

    const { selectedFilters } = useContext(FiltersContext)

    useEffect(() => {
        if (!headerRef.current || !dropdownRef.current) {
            return
        }
        computePosition(headerRef.current, dropdownRef.current, {
            placement: "bottom-start",
            middleware: [flip(), shift()],
        }).then(({ x, y }) => {
            if (dropdownRef.current?.style) {
                Object.assign(dropdownRef.current.style, {
                    top: `${y + 5}px`,
                    left: `${x}px`,
                    visibility: "visible",
                })
            }
        })
    }, [visible, header.filterMenu])

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            if (headerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return
            setVisible(false)
        }
        const timeout = setTimeout(() => document.addEventListener("click", handleClickOutside), 0)
        return () => {
            clearTimeout(timeout)
            document.removeEventListener("click", handleClickOutside)
        }
    }, [])

    const handleBottomReached = () => {
        onBottomReached?.()
    }

    const filteredSelectedHeaders =
        (selectedFilters
            ?.filter((f) => f.fromHeader === header.value)
            ?.map((f) => ({
                value: f.filter,
                valueToDisplay: f.valueToDisplay,
            }))
            .sort((a, b) => a.value.toLowerCase().localeCompare(b.value.toLowerCase())) as FilterMenuItem[]) || []

    const filteredHeaders =
        header.filterMenu?.filter((f) => !selectedFilters?.find((sf) => sf.filter === f.value)) || []

    const onClose = () => {
        setVisible(false)
    }

    return (
        <th
            ref={headerRef}
            className="flexible-table__header__cell flexible-table__header__cell--pointer"
            onClick={(e) => {
                // El dropdown se portalea, pero en el árbol de React sigue siendo hijo de este <th>,
                // así que sus clicks internos (buscador, limpiar, padding) llegan hasta aquí
                if (dropdownRef.current?.contains(e.target as Node)) return
                setVisible((prev) => !prev)
            }}
        >
            <div
                style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "5px",
                }}
            >
                <span className="flexible-table__header__cell-text">{header.valueToDisplay || header.value}</span>
                {filteredSelectedHeaders.length ? (
                    <Icon name="filterFill" color="var(--primary)" height={"14px"} width={"14px"} />
                ) : (
                    <Icon name={"chevronUp"} color="var(--purple-drawer-primario)" height={"20px"} width={"20px"} />
                )}
            </div>
            {visible && ReactDOM.createPortal(
                <div
                    ref={dropdownRef}
                    style={{ position: "absolute", top: 0, left: 0, visibility: "hidden" }}
                >
                    <Suggestions
                        onClose={onClose}
                        onBottomReached={handleBottomReached}
                        ref={scrollRef}
                        options={
                            header.filterMenu?.length
                                ? [...filteredSelectedHeaders, ...filteredHeaders]
                                : // : [{ valueToDisplay: "Todos", value: "todos" }]
                                  [...filteredSelectedHeaders]
                        }
                        debounceSearchMSTime={debounceSearchMSTime}
                        header={header}
                        onInputFilterSuggestionChange={header.onInputFilterSuggestionChange}
                        onChange={(value, valueToDisplay) => {
                            onSelectFilter({
                                filter: value,
                                fromHeader: header.value,
                                valueToDisplay,
                                isUnique: header.isFilterUnique,
                            })
                        }}
                    />
                </div>,
                portalLayer
            )}
        </th>
    )
}

export default HeaderSuggetions
